/**
 * Pipeline State & Artifact Management Tools — "Sinks Not Pipes"
 *
 * The filesystem IS the state machine. Every tool reads/writes to `.stackwright/`.
 * Specialists produce artifacts → validated here. Retry logic lives HERE.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readFileSync, writeFileSync, existsSync, mkdirSync, lstatSync } from 'fs';
import { lockSync } from 'proper-lockfile';
import { join } from 'path';
import { parseLLMQuestionsResponse } from '../question-adapter.js';
import { boolCoerce, jsonCoerce } from '../coerce.js';
import { createHash } from 'crypto';
import {
  initPipelineKeys,
  loadPipelineKeys,
  signArtifact,
  verifyArtifact,
  getArtifactSignature,
  saveArtifactSignature,
  emitSignatureAuditEvent,
  loadSignatureManifest,
} from '../artifact-signing.js';
import { SAFE_PHASE } from '../validation.js';
import { validatePulseContent, formatPulseRetryPrompt } from './validate-pulse-content.js';

// ─── Canonical pro type schemas for artifact validation ─────────────────────
import { WorkflowFileSchema, authConfigSchema } from '@stackwright-pro/types';
import { buildSchemaReferenceBlock } from './get-schema.js';
import { loadPipelineGraph } from './pipeline-graph.js';
import { emit } from '@stackwright-pro/telemetry';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Phases that generate `pages/**\/content.yml` files and therefore need
 * Pulse component schema validation at artifact-write time.
 * (swp-l6yu — structural fix for the prop-name whack-a-mole pattern)
 */
const PULSE_CONTENT_PHASES = new Set<string>(['pages', 'dashboard', 'geo', 'workflow']);

/**
 * Canonical phase execution order — must remain a valid topological sort of
 * the otter pipeline declarations. Validated at server startup by server.ts.
 *
 * Wave decomposition (derived from loadPipelineGraph()):
 *   Wave 1: designer, api                           (no deps)
 *   Wave 2: theme, auth, data                       (designer or api only)
 *   Wave 3: scaffold, workflow, geo                 (theme / theme+api / data)
 *   Wave 4: services                                (api + workflow)
 *   Wave 5: pages, dashboard                        (all above)
 *   Wave 6: polish                                  (pages + dashboard + workflow + auth)
 *   Wave 7: qa                                      (depends on polish + transitively everything)
 *
 * Key change from the old hardcoded PHASE_DEPENDENCIES:
 *   auth: was wave 4 (after pages/dashboard/workflow/geo); now wave 2 (needs designer only)
 *   workflow: was wave 1 (no deps); now wave 3 (needs theme + api)
 */
export const PHASE_ORDER = [
  'designer',
  'theme',
  'scaffold', // generates app/ directory from config
  'api',
  'data',
  'auth', // moved earlier — only depends on design-language.json (designer)
  'geo',
  'workflow',
  'services',
  'pages',
  'dashboard',
  'polish',
  'qa',
] as const;
export type Phase = (typeof PHASE_ORDER)[number];

/** Maps phase → expected artifact filename */
export const PHASE_ARTIFACT: Record<Phase, string> = {
  designer: 'design-language.json',
  theme: 'theme-tokens.json',
  scaffold: 'scaffold-manifest.json',
  api: 'api-config.json',
  auth: 'auth-config.json',
  data: 'data-config.json',
  pages: 'pages-manifest.json',
  dashboard: 'dashboard-manifest.json',
  workflow: 'workflow-config.json',
  services: 'services-config.json',
  polish: 'polish-manifest.json',
  geo: 'geo-manifest.json',
  qa: 'qa-findings.json',
};

/** Maps phase → otter agent name */
const PHASE_TO_OTTER: Record<Phase, string> = {
  designer: 'stackwright-pro-designer-otter',
  theme: 'stackwright-pro-theme-otter',
  scaffold: 'stackwright-pro-scaffold-otter',
  api: 'stackwright-pro-api-otter',
  auth: 'stackwright-pro-auth-otter',
  data: 'stackwright-pro-data-otter',
  pages: 'stackwright-pro-page-otter',
  dashboard: 'stackwright-pro-dashboard-otter',
  workflow: 'stackwright-pro-form-wizard-otter',
  services: 'stackwright-services-otter',
  polish: 'stackwright-pro-polish-otter',
  geo: 'stackwright-pro-geo-otter',
  qa: 'stackwright-pro-qa-otter',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PhaseStatus {
  questionsCollected: boolean;
  answered: boolean;
  executed: boolean;
  artifactWritten: boolean;
  retryCount: number;
  /** True while specialist is being invoked; prevents double-fire in dataflow mode (swp-ioc7). */
  inFlight?: boolean;
}

export interface PipelineState {
  version: '1.0';
  currentPhase: string;
  status: 'setup' | 'questions' | 'execution' | 'done';
  phases: Record<string, PhaseStatus>;
  startedAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidPhase(phase: string): phase is Phase {
  return (PHASE_ORDER as readonly string[]).includes(phase);
}

function defaultPhaseStatus(): PhaseStatus {
  return {
    questionsCollected: false,
    answered: false,
    executed: false,
    artifactWritten: false,
    retryCount: 0,
    inFlight: false,
  };
}

function createDefaultState(): PipelineState {
  const now = new Date().toISOString();
  const phases: Record<string, PhaseStatus> = {};
  for (const p of PHASE_ORDER) {
    phases[p] = defaultPhaseStatus();
  }
  return {
    version: '1.0',
    currentPhase: PHASE_ORDER[0],
    status: 'setup',
    phases,
    startedAt: now,
    updatedAt: now,
  };
}

function statePath(cwd: string): string {
  return join(cwd, '.stackwright', 'pipeline-state.json');
}

function readState(cwd: string): PipelineState {
  const p = statePath(cwd);
  if (!existsSync(p)) return createDefaultState();
  const raw = JSON.parse(safeReadSync(p));
  // Minimal schema validation — reject corrupted state files
  if (typeof raw !== 'object' || raw === null || raw.version !== '1.0') {
    return createDefaultState();
  }
  return raw as PipelineState;
}

/**
 * Safely write to a path, rejecting symlinks.
 * Used by all pipeline write operations.
 */
function safeWriteSync(filePath: string, content: string): void {
  if (existsSync(filePath)) {
    const stat = lstatSync(filePath);
    if (stat.isSymbolicLink()) {
      throw new Error(`Refusing to write to symlink: ${filePath}`);
    }
  }
  writeFileSync(filePath, content);
}

/**
 * Safely read from a path, rejecting symlinks.
 * Used by all pipeline read operations to prevent TOCTOU-based symlink attacks.
 */
function safeReadSync(filePath: string): string {
  if (existsSync(filePath)) {
    const stat = lstatSync(filePath);
    if (stat.isSymbolicLink()) {
      throw new Error(`Refusing to read symlink: ${filePath}`);
    }
  }
  return readFileSync(filePath, 'utf-8');
}

function writeState(cwd: string, state: PipelineState): void {
  const dir = join(cwd, '.stackwright');
  mkdirSync(dir, { recursive: true });
  state.updatedAt = new Date().toISOString();
  safeWriteSync(statePath(cwd), JSON.stringify(state, null, 2) + '\n');
}

/**
 * Atomically read-modify-write pipeline-state.json with a file-level lock.
 *
 * Required when concurrent specialists may call validate_artifact, write_phase_questions,
 * or set_pipeline_state simultaneously (see swp-9vuj parallel phase execution). Without
 * this, a read-then-write race can silently overwrite another specialist's state mutation.
 *
 * The mutator receives the freshest state from disk (after lock is acquired) and mutates
 * it in place. After the mutator returns, writeState() is called and the lock released.
 *
 * Lock retries with brief backoff for up to ~2 seconds. Throws if lock can't be acquired.
 */
function updateStateAtomic(cwd: string, mutator: (state: PipelineState) => void): PipelineState {
  const dir = join(cwd, '.stackwright');
  mkdirSync(dir, { recursive: true });
  // Ensure the file exists so proper-lockfile can lock on it
  const path = statePath(cwd);
  if (!existsSync(path)) {
    safeWriteSync(path, JSON.stringify(createDefaultState(), null, 2) + '\n');
  }
  const release = lockSync(path, {
    realpath: false,
    stale: 10_000, // Treat locks older than 10s as stale (guards against crash mid-lock)
  });
  try {
    const state = readState(cwd);
    mutator(state);
    writeState(cwd, state);
    return state;
  } finally {
    release();
  }
}

/** Extract JSON from messy LLM output — handles markdown fences, leading prose */
function extractJsonFromResponse(text: string): unknown {
  let cleaned = text;
  // Strip markdown code fences (```json ... ``` or ``` ... ```)
  cleaned = cleaned.replace(/```(?:json)?\s*/gi, '');
  cleaned = cleaned.replace(/```\s*$/gm, '');

  // Find the outermost JSON object
  const firstBrace = cleaned.indexOf('{');
  if (firstBrace === -1) throw new Error('No JSON object found in response');
  cleaned = cleaned.substring(firstBrace);

  const lastBrace = cleaned.lastIndexOf('}');
  if (lastBrace === -1) throw new Error('Unclosed JSON object in response');
  cleaned = cleaned.substring(0, lastBrace + 1);

  // Fix common LLM JSON quirks
  cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1'); // trailing commas
  return JSON.parse(cleaned);
}

// ---------------------------------------------------------------------------
// Handler functions (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

export function handleGetPipelineState(_cwd?: string): { text: string; isError: boolean } {
  const cwd = _cwd ?? process.cwd();
  try {
    const state = readState(cwd);

    // Auto-initialize signing keys on first access (lazy init)
    const keyPath = join(cwd, '.stackwright', 'pipeline-keys.json');
    if (!existsSync(keyPath)) {
      try {
        const { fingerprint } = initPipelineKeys(cwd);
        // Record key fingerprint in pipeline state for audit trail
        (state as unknown as Record<string, unknown>)['signingKeyFingerprint'] = fingerprint;
        writeState(cwd, state);
      } catch {
        // Non-blocking — signing is defense-in-depth, not a hard gate at init
      }
    }

    return { text: JSON.stringify(state), isError: false };
  } catch (err) {
    return { text: JSON.stringify({ error: true, message: String(err) }), isError: true };
  }
}

export type PhaseBooleanField =
  | 'questionsCollected'
  | 'answered'
  | 'executed'
  | 'artifactWritten'
  | 'inFlight';

export interface BatchStateUpdate {
  phase: string;
  field: PhaseBooleanField;
  value: boolean;
}

export interface SetPipelineStateInput {
  phase?: string;
  field?: PhaseBooleanField;
  value?: boolean;
  status?: 'setup' | 'questions' | 'execution' | 'done';
  incrementRetry?: boolean;
  updates?: BatchStateUpdate[];
  _cwd?: string;
}

export function handleSetPipelineState(input: SetPipelineStateInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();

  // Validate phase name if provided
  if (input.phase && !isValidPhase(input.phase)) {
    return {
      text: JSON.stringify({
        error: true,
        message: `Invalid phase: ${input.phase}. Valid phases are: ${PHASE_ORDER.join(', ')}`,
      }),
      isError: true,
    };
  }

  // Validate field name if provided
  const VALID_FIELDS = [
    'questionsCollected',
    'answered',
    'executed',
    'artifactWritten',
    'inFlight',
  ] as const;
  if (input.field && !VALID_FIELDS.includes(input.field as (typeof VALID_FIELDS)[number])) {
    return {
      text: JSON.stringify({
        error: true,
        message: `Invalid field: ${input.field}. Valid fields are: ${VALID_FIELDS.join(', ')}`,
      }),
      isError: true,
    };
  }

  // Validate batch updates before touching state
  if (input.updates && input.updates.length > 0) {
    for (const update of input.updates) {
      if (!isValidPhase(update.phase)) {
        return {
          text: JSON.stringify({
            error: true,
            message: `Invalid phase in batch update: ${update.phase}. Valid phases are: ${PHASE_ORDER.join(', ')}`,
          }),
          isError: true,
        };
      }
      if (!VALID_FIELDS.includes(update.field as (typeof VALID_FIELDS)[number])) {
        return {
          text: JSON.stringify({
            error: true,
            message: `Invalid field in batch update: ${update.field}. Valid fields are: ${VALID_FIELDS.join(', ')}`,
          }),
          isError: true,
        };
      }
    }
  }

  try {
    const state = updateStateAtomic(cwd, (state) => {
      // Update top-level status if provided
      if (input.status) {
        state.status = input.status;
      }

      // Update phase-level fields
      if (input.phase) {
        const phase = input.phase;
        if (!state.phases[phase]) {
          state.phases[phase] = defaultPhaseStatus();
        }
        const phaseState = state.phases[phase];

        if (input.field && input.value !== undefined) {
          phaseState[input.field] = input.value;
        }

        if (input.incrementRetry) {
          phaseState.retryCount += 1;
        }

        state.currentPhase = phase;
      }

      // Apply batch updates in the same read→modify→write cycle
      if (input.updates && input.updates.length > 0) {
        for (const update of input.updates) {
          if (!state.phases[update.phase]) {
            state.phases[update.phase] = defaultPhaseStatus();
          }
          const batchPhaseState = state.phases[update.phase]!;
          batchPhaseState[update.field] = update.value;
          state.currentPhase = update.phase;
        }
      }
    });

    // Telemetry: best-effort, never blocks (swp-im3c)
    try {
      const completedPhases = Object.entries(state.phases)
        .filter(([, ps]) => ps.artifactWritten)
        .map(([p]) => p);
      emit(
        {
          type: 'pipeline_state_change',
          state: state.status,
          phases: { ready: [], completed: completedPhases, blocked: [] },
        },
        { cwd }
      );
      // If a specific phase just had artifactWritten set to true, emit phase_complete
      if (input.field === 'artifactWritten' && input.value === true && input.phase) {
        emit({ type: 'phase_complete', phase: input.phase }, { cwd });
      }
      // Batch updates: emit phase_complete for any phase that just had artifactWritten set
      if (input.updates) {
        for (const update of input.updates) {
          if (update.field === 'artifactWritten' && update.value === true) {
            emit({ type: 'phase_complete', phase: update.phase }, { cwd });
          }
        }
      }
    } catch {
      // Telemetry errors never surface to callers
    }

    return { text: JSON.stringify(state), isError: false };
  } catch (err) {
    return { text: JSON.stringify({ error: true, message: String(err) }), isError: true };
  }
}

export function handleCheckExecutionReady(
  _cwd?: string,
  phase?: string
): { text: string; isError: boolean } {
  const cwd = _cwd ?? process.cwd();

  if (phase) {
    if (!isValidPhase(phase)) {
      return {
        text: JSON.stringify({
          error: true,
          message: `Invalid phase: ${phase}. Valid phases are: ${PHASE_ORDER.join(', ')}`,
        }),
        isError: true,
      };
    }
    const answerFile = join(cwd, '.stackwright', 'answers', `${phase}.json`);
    if (!existsSync(answerFile)) {
      return {
        text: JSON.stringify({ ready: false, phase, reason: 'Answer file not found' }),
        isError: false,
      };
    }
    try {
      const raw = safeReadSync(answerFile);
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (
        typeof parsed['version'] !== 'string' ||
        typeof parsed['phase'] !== 'string' ||
        typeof parsed['answers'] !== 'object' ||
        parsed['answers'] === null
      ) {
        return {
          text: JSON.stringify({ ready: false, phase, reason: 'Answer file is malformed' }),
          isError: false,
        };
      }
      return { text: JSON.stringify({ ready: true, phase }), isError: false };
    } catch {
      return {
        text: JSON.stringify({ ready: false, phase, reason: 'Answer file could not be parsed' }),
        isError: false,
      };
    }
  }

  try {
    const answersDir = join(cwd, '.stackwright', 'answers');
    const answeredPhases: string[] = [];
    const missingPhases: string[] = [];

    for (const phase of PHASE_ORDER) {
      const answerFile = join(answersDir, `${phase}.json`);
      if (existsSync(answerFile)) {
        // Validate: file must be parseable JSON with required fields
        try {
          const raw = safeReadSync(answerFile);
          const parsed = JSON.parse(raw) as Record<string, unknown>;

          // Must have version, phase, and answers keys
          if (
            typeof parsed['version'] !== 'string' ||
            typeof parsed['phase'] !== 'string' ||
            typeof parsed['answers'] !== 'object' ||
            parsed['answers'] === null
          ) {
            // File exists but is malformed — treat as missing
            missingPhases.push(phase);
            continue;
          }

          answeredPhases.push(phase);
        } catch {
          // Can't parse — treat as missing
          missingPhases.push(phase);
        }
      } else {
        missingPhases.push(phase);
      }
    }

    return {
      text: JSON.stringify({
        ready: missingPhases.length === 0,
        answeredPhases,
        missingPhases,
        totalPhases: PHASE_ORDER.length,
      }),
      isError: false,
    };
  } catch (err) {
    return { text: JSON.stringify({ error: true, message: String(err) }), isError: true };
  }
}

/**
 * Return phases whose dependencies are all satisfied (executed === true in
 * pipeline state). The foreman calls this in a loop to discover which phases
 * can run next — enabling wave-based execution instead of strict serial order.
 *
 * Wave decomposition (derived from otter pipeline declarations — see PHASE_ORDER):
 *   Wave 1: designer, api
 *   Wave 2: theme, auth, data
 *   Wave 3: scaffold, workflow, geo
 *   Wave 4: services
 *   Wave 5: pages, dashboard
 *   Wave 6: polish
 */
export function handleGetReadyPhases(_cwd?: string): { text: string; isError: boolean } {
  const cwd = _cwd ?? process.cwd();
  try {
    const state = readState(cwd);
    const graph = loadPipelineGraph();

    const completed: string[] = [];
    const ready: string[] = [];
    const blocked: string[] = [];

    for (const phase of PHASE_ORDER) {
      const ps = state.phases[phase];
      if (ps?.executed) {
        completed.push(phase);
        continue;
      }
      // Phase is ready if every dependency is executed
      const deps = graph.dependencies[phase] ?? [];
      const unmetDeps = deps.filter((dep) => !state.phases[dep]?.executed);
      if (unmetDeps.length === 0) {
        ready.push(phase);
      } else {
        blocked.push(phase);
      }
    }

    return {
      text: JSON.stringify({
        readyPhases: ready,
        completedPhases: completed,
        blockedPhases: blocked,
        waveSize: ready.length,
        allComplete: ready.length === 0 && blocked.length === 0,
      }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { text: JSON.stringify({ error: true, message }), isError: true };
  }
}

export function handleListArtifacts(_cwd?: string): { text: string; isError: boolean } {
  const cwd = _cwd ?? process.cwd();
  try {
    const artifactsDir = join(cwd, '.stackwright', 'artifacts');

    // Load signature manifest (non-blocking if missing)
    let manifest: ReturnType<typeof loadSignatureManifest> | null = null;
    try {
      manifest = loadSignatureManifest(cwd);
    } catch {
      // No signature manifest — report unsigned
    }

    const artifacts: Array<{
      phase: string;
      expectedFile: string;
      exists: boolean;
      path: string;
      signed: boolean;
      signatureValid: boolean | null;
    }> = [];
    let completedCount = 0;

    for (const phase of PHASE_ORDER) {
      const expectedFile = PHASE_ARTIFACT[phase];
      const fullPath = join(artifactsDir, expectedFile);
      const exists = existsSync(fullPath);
      if (exists) completedCount++;

      // Check signature status
      let signed = false;
      let signatureValid: boolean | null = null;

      if (exists && manifest) {
        const entry = manifest.signatures[expectedFile];
        if (entry) {
          signed = true;
          // Verify if keys are available
          try {
            const rawBytes = Buffer.from(safeReadSync(fullPath), 'utf-8');
            const { publicKey } = loadPipelineKeys(cwd);
            signatureValid = verifyArtifact(rawBytes, entry, publicKey);
          } catch {
            signatureValid = null; // Can't verify — keys unavailable
          }
        }
      }

      artifacts.push({ phase, expectedFile, exists, path: fullPath, signed, signatureValid });
    }

    return {
      text: JSON.stringify({ artifacts, completedCount, totalCount: PHASE_ORDER.length }),
      isError: false,
    };
  } catch (err) {
    return { text: JSON.stringify({ error: true, message: String(err) }), isError: true };
  }
}

export interface WritePhaseQuestionsInput {
  phase: string;
  responseText: string;
  _cwd?: string;
}

export function handleWritePhaseQuestions(input: WritePhaseQuestionsInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const { phase, responseText } = input;

  if (!isValidPhase(phase)) {
    return {
      text: JSON.stringify({ error: true, message: `Unknown phase: ${phase}` }),
      isError: true,
    };
  }

  try {
    const questions = parseLLMQuestionsResponse(responseText);

    // Extract requiredPackages if present in the raw response
    let requiredPackages: {
      dependencies: Record<string, string>;
      devPackages: Record<string, string>;
    } = {
      dependencies: {},
      devPackages: {},
    };
    try {
      const fullParsed = extractJsonFromResponse(responseText) as Record<string, unknown>;
      if (fullParsed.requiredPackages && typeof fullParsed.requiredPackages === 'object') {
        const rp = fullParsed.requiredPackages as Record<string, unknown>;
        requiredPackages = {
          dependencies: (rp.dependencies as Record<string, string>) ?? {},
          devPackages: (rp.devPackages as Record<string, string>) ?? {},
        };
      }
    } catch {
      // extractJsonFromResponse may fail if the response is a bare array — that's fine
    }

    const questionsDir = join(cwd, '.stackwright', 'questions');
    mkdirSync(questionsDir, { recursive: true });
    const filePath = join(questionsDir, `${phase}.json`);

    const payload = {
      version: '1.0',
      phase,
      otter: PHASE_TO_OTTER[phase],
      collectedAt: new Date().toISOString(),
      questions,
      requiredPackages,
    };

    safeWriteSync(filePath, JSON.stringify(payload, null, 2) + '\n');

    // Update pipeline state
    updateStateAtomic(cwd, (state) => {
      if (!state.phases[phase]) state.phases[phase] = defaultPhaseStatus();
      const ps = state.phases[phase]!; // guaranteed by line above
      ps.questionsCollected = true;
    });

    return {
      text: JSON.stringify({
        success: true,
        phase,
        questionCount: questions.length,
        requiredPackages,
        path: filePath,
      }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { text: JSON.stringify({ error: true, phase, message }), isError: true };
  }
}

export interface BuildSpecialistPromptInput {
  phase: string;
  _cwd?: string;
}

export function handleBuildSpecialistPrompt(input: BuildSpecialistPromptInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const { phase } = input;

  if (!isValidPhase(phase)) {
    return {
      text: JSON.stringify({ error: true, message: `Unknown phase: ${phase}` }),
      isError: true,
    };
  }

  try {
    // 1. Read answers for this phase
    const answersPath = join(cwd, '.stackwright', 'answers', `${phase}.json`);
    let answers: unknown = {};
    if (existsSync(answersPath)) {
      answers = JSON.parse(safeReadSync(answersPath));
    }

    // 1b. Read build context (optional — non-blocking if missing or malformed)
    let buildContextText = '';
    const buildContextPath = join(cwd, '.stackwright', 'build-context.json');
    if (existsSync(buildContextPath)) {
      try {
        const bcRaw = JSON.parse(safeReadSync(buildContextPath)) as { buildContext?: string };
        if (typeof bcRaw.buildContext === 'string' && bcRaw.buildContext.trim().length > 0) {
          buildContextText = bcRaw.buildContext.trim();
        }
      } catch {
        // Non-blocking — missing or malformed build context does not block execution
      }
    }

    // 2. Read upstream dependency artifacts
    const graph = loadPipelineGraph();
    const deps = graph.dependencies[phase] ?? [];
    const artifactSections: string[] = [];
    const missingDependencies: string[] = [];

    for (const dep of deps) {
      const artifactFile = PHASE_ARTIFACT[dep as Phase];
      const artifactPath = join(cwd, '.stackwright', 'artifacts', artifactFile);
      if (existsSync(artifactPath)) {
        const rawContent = safeReadSync(artifactPath);
        const rawBytes = Buffer.from(rawContent, 'utf-8');
        const content = JSON.parse(rawContent) as Record<string, unknown>;

        // ── Cryptographic signature verification (primary integrity check) ──
        // If signing keys exist, verify the ECDSA P-384 signature before trusting
        // the artifact. Falls back to generatedBy check if unsigned (backwards compat).
        let signatureVerified = false;
        let signatureAvailable = false;

        try {
          const sig = getArtifactSignature(cwd, artifactFile);
          if (sig) {
            signatureAvailable = true;
            const { publicKey } = loadPipelineKeys(cwd);
            signatureVerified = verifyArtifact(rawBytes, sig, publicKey);

            if (!signatureVerified) {
              // Emit audit event for SOC2/FedRAMP compliance
              const actualDigest = createHash('sha384').update(rawBytes).digest('hex');
              emitSignatureAuditEvent({
                artifactFilename: artifactFile,
                expectedDigest: sig.digest,
                actualDigest,
                phase: dep,
                timestamp: new Date().toISOString(),
                source: 'stackwright_pro_build_specialist_prompt',
              });

              missingDependencies.push(dep);
              artifactSections.push(
                `[${artifactFile}]:\n(integrity check failed: ECDSA-P384 signature verification failed — artifact may have been tampered with)`
              );
              continue;
            }
          }
        } catch {
          // Key loading failed — fall through to generatedBy check
        }

        // ── Secondary check: generatedBy field cross-validation ──
        // Even with valid signatures, verify the generatedBy field matches expectations.
        // Without signatures (unsigned pipeline), this is the primary check.
        const expectedOtter = PHASE_TO_OTTER[dep as Phase];
        const artifactOtter = content['generatedBy'] as string | undefined;
        const normalizedOtter = artifactOtter?.replace(/-[a-f0-9]{6}$/, '');

        if (!artifactOtter) {
          missingDependencies.push(dep);
          artifactSections.push(
            `[${artifactFile}]:\n(integrity check failed: missing generatedBy field)`
          );
        } else if (normalizedOtter !== expectedOtter) {
          missingDependencies.push(dep);
          artifactSections.push(
            `[${artifactFile}]:\n(integrity check failed: artifact claims generatedBy="${artifactOtter}" but expected="${expectedOtter}")`
          );
        } else {
          const sigStatus = signatureAvailable
            ? signatureVerified
              ? ' [signature verified]'
              : ' [signature check skipped]'
            : ' [unsigned]';
          artifactSections.push(
            `[${artifactFile}]${sigStatus}:\n${JSON.stringify(content, null, 2)}`
          );
        }
      } else {
        missingDependencies.push(dep);
        artifactSections.push(`[${artifactFile}]:\n(not yet available)`);
      }
    }

    // 3. Assemble the prompt
    const parts: string[] = [];

    if (buildContextText) {
      parts.push('BUILD_CONTEXT:', buildContextText, '');
    }

    // For auth phase: inject devOnly flag from init-context so the auth otter
    // always passes it to configure_auth — prevents prebuild crash (env var refs in dev mode)
    if (phase === 'auth') {
      const initContextPath = join(cwd, '.stackwright', 'init-context.json');
      if (existsSync(initContextPath)) {
        try {
          const initContext = JSON.parse(safeReadSync(initContextPath));
          if (initContext.devOnly === true || initContext.nonInteractive === true) {
            (answers as Record<string, unknown>)['devOnly'] = true;
          }
        } catch {
          // Non-blocking — auth otter can still infer from BUILD_CONTEXT
        }
      }
    }

    parts.push('ANSWERS:', JSON.stringify(answers, null, 2));

    if (artifactSections.length > 0) {
      parts.push('', 'UPSTREAM ARTIFACTS:', '', ...artifactSections);
    }

    // Inject the canonical artifact schema so specialists always build against
    // the current contract — not hardcoded examples in their JSON files.
    const artifactSchema = PHASE_ARTIFACT_SCHEMA[phase];
    parts.push('', 'REQUIRED_ARTIFACT_SCHEMA:');
    parts.push(artifactSchema);

    // Inject canonical field-name schema reference for phases that produce
    // YAML with strict schemas (workflow, auth, pages/navigation). This closes
    // the schema-prompt impedance mismatch identified across 5 postmortems —
    // specialists receive the actual Zod schema, not a prose copy that can drift.
    const schemaRef = buildSchemaReferenceBlock(phase);
    if (schemaRef) {
      parts.push('', schemaRef);
    }

    parts.push('', 'Execute using these answers and the upstream artifacts provided.');

    const prompt = parts.join('\n');
    const dependenciesSatisfied = missingDependencies.length === 0;

    return {
      text: JSON.stringify({
        otterName: PHASE_TO_OTTER[phase],
        phase,
        prompt,
        dependenciesSatisfied,
        missingDependencies,
      }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { text: JSON.stringify({ error: true, phase, message }), isError: true };
  }
}

export interface ValidateArtifactInput {
  phase: string;
  responseText?: string;
  artifact?: Record<string, unknown>;
  /** When phase='api', write to api-config-<integrationName>.json instead of api-config.json */
  integrationName?: string;
  _cwd?: string;
}

const OFF_SCRIPT_PATTERNS: Array<{ pattern: RegExp; label: string }> = [
  {
    pattern: /```(?:ts|tsx|js|jsx|python|bash|sh|sql|ruby|go|rust|java|csharp|c\+\+)\b/,
    label: 'code fence',
  },
  { pattern: /\bimport\s+[\w{]/, label: 'import statement' },
  { pattern: /\bexport\s+(?:const|function|default|class)\b/, label: 'export statement' },
  { pattern: /\brequire\s*\(/, label: 'require() call' },
  { pattern: /\beval\s*\(/, label: 'eval() call' },
  { pattern: /^#!/m, label: 'shebang' },
  { pattern: /<script[\s>]/i, label: 'script tag' },
  { pattern: /\.(ts|tsx|js|jsx)\b.*\bfile\b/i, label: 'code file reference' },
];

interface ValidArtifactResult {
  valid: true;
  phase: string;
  artifactPath: string;
  summary: string;
}

interface InvalidArtifactResult {
  valid: false;
  phase: string;
  violation: 'off-script' | 'invalid-json' | 'missing-fields' | 'schema-mismatch' | 'rbac-theatre';
  retryPrompt: string;
}

const PHASE_REQUIRED_KEYS: Record<Phase, string[]> = {
  designer: ['designLanguage', 'themeTokenSeeds'],
  theme: ['tokens'],
  scaffold: ['version', 'generatedBy', 'appRouterFiles'],
  api: ['entities', 'version', 'generatedBy'],
  auth: ['version', 'generatedBy'],
  data: ['version', 'generatedBy', 'strategy', 'collections'],
  pages: ['version', 'generatedBy'],
  dashboard: ['version', 'generatedBy'],
  workflow: ['version', 'generatedBy'],
  services: ['version', 'generatedBy', 'flows'],
  polish: ['version', 'generatedBy'],
  geo: ['version', 'generatedBy', 'geoCollections'],
  // qa: skipped=true path only needs version+generatedBy+skipped;
  // skipped=false path also needs summary+findings — Zod validator enforces the conditional.
  qa: ['version', 'generatedBy', 'skipped'],
};

/**
 * Canonical artifact schema examples for each phase.
 *
 * SINGLE SOURCE OF TRUTH — injected into specialist prompts at invocation time
 * via handleBuildSpecialistPrompt. Both validate_artifact (enforcement) and
 * build_specialist_prompt (guidance) derive from the same constant here.
 *
 * Schema changes happen in THIS file only. Zero drift possible.
 */
const PHASE_ARTIFACT_SCHEMA: Record<Phase, string> = {
  designer: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-designer-otter',
      application: {
        type: '<operational|data-explorer|admin|logistics|general>',
        environment: '<workstation|field|control-room|mixed>',
        density: '<compact|balanced|spacious>',
        accessibility: '<wcag-aa|section-508|none>',
        colorScheme: '<light|dark|both>',
      },
      designLanguage: {
        rationale: '<design rationale>',
        spacingScale: { base: 8, scale: [0, 4, 8, 16, 24, 32, 48, 64] },
        colorSemantics: { primary: '#1a365d', accent: '#e53e3e' },
        typography: {
          dataFont: 'Inter',
          headingFont: 'Inter',
          monoFont: 'monospace',
          dataSizePx: 12,
          bodySizePx: 14,
        },
        contrastRatio: '4.5',
        borderRadius: '4',
        shadowElevation: 'standard',
      },
      themeTokenSeeds: {
        light: {
          background: '#ffffff',
          foreground: '#1a1a1a',
          primary: '#1a365d',
          surface: '#f7f7f7',
          border: '#e2e8f0',
        },
        dark: {
          background: '#1a1a1a',
          foreground: '#ffffff',
          primary: '#90cdf4',
          surface: '#2d2d2d',
          border: '#4a5568',
        },
      },
      conformsTo: null,
      operationalNotes: [],
    },
    null,
    2
  ),

  theme: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-theme-otter',
      componentLibrary: 'shadcn',
      colorScheme: '<light|dark|both>',
      tokens: {
        colors: { 'primary-500': '#1a365d', background: '#ffffff' },
        spacing: { 'spacing-1': '8px', 'spacing-2': '16px' },
        typography: { 'font-data': 'Inter', 'text-sm': '12px' },
        shape: { 'radius-sm': '4px', 'radius-md': '8px' },
        shadows: { 'shadow-sm': '0 1px 2px rgba(0,0,0,0.08)' },
      },
      cssVariables: {
        '--background': '0 0% 100%',
        '--foreground': '222.2 84% 4.9%',
        '--primary': '222.2 47.4% 11.2%',
        '--primary-foreground': '210 40% 98%',
        '--surface': '210 40% 98%',
        '--border': '214.3 31.8% 91.4%',
      },
      dark: { '--background': '222.2 84% 4.9%', '--foreground': '210 40% 98%' },
    },
    null,
    2
  ),

  scaffold: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-scaffold-otter',
      // REQUIRED: appRouterFiles is a required key — NOT 'files' (see swp-k3mb)
      appRouterFiles: [
        'app/layout.tsx',
        'app/_components/page-client.tsx',
        'app/page.tsx',
        'app/[...slug]/page.tsx',
        'app/_components/providers.tsx',
        'app/not-found.tsx',
      ],
      title: '<title from stackwright.yml>',
      hasCollectionEndpoints: false,
      hasAuthConfig: true,
    },
    null,
    2
  ),

  api: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-api-otter',
      entities: [
        {
          name: 'Shipment',
          endpoint: '/shipments',
          method: 'GET',
          revalidate: 60,
          mutationType: null,
        },
      ],
      skipped: [
        {
          spec: 'ais-message-models.yaml',
          format: 'asyncapi',
          reason: 'AsyncAPI spec — WebSocket/event integration required (no REST endpoints)',
        },
      ],
      warnings: [
        'Spec contains external $ref URLs — recommend bundle: true in stackwright.yml integration config',
      ],
      auth: { type: 'bearer', header: 'Authorization', envVar: 'API_TOKEN' },
      baseUrl: 'https://api.example.mil/v2',
      specPath: './specs/api.yaml',
    },
    null,
    2
  ),

  data: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-data-otter',
      strategy: '<pulse-fast|isr-fast|isr-standard|isr-slow>',
      pulseMode: false,
      collections: [{ name: 'equipment', revalidate: 60, pulse: false }],
      endpoints: { included: ['/equipment/**'], excluded: ['/admin/**'] },
      requiredPackages: { dependencies: {}, devPackages: {} },
    },
    null,
    2
  ),

  geo: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-geo-otter',
      geoCollections: [
        {
          collection: 'vessels',
          latField: 'latitude',
          lngField: 'longitude',
          labelField: 'vesselName',
          colorField: 'navigationStatus',
        },
      ],
      pages: [
        {
          slug: 'fleet-tracker',
          type: '<tracker|zone|route|combined>',
          collections: ['vessels'],
          hasLayers: false,
        },
      ],
    },
    null,
    2
  ),

  workflow: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-form-wizard-otter',
      // Root key is `workflow` — NOT `workflowConfig` (see swp-k7cl)
      workflow: {
        id: 'procurement-approval',
        route: '/procurement',
        files: ['workflows/procurement-approval.yml'],
        serviceDependencies: ['service:workflow-state'],
        warnings: [],
      },
    },
    null,
    2
  ),

  services: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-services-otter',
      flows: [
        {
          name: 'at-risk-patients',
          trigger: '<http|event|schedule|queue>',
          steps: 3,
          outputSpec: 'at-risk-patients.openapi.json',
        },
      ],
      workflows: [
        {
          name: 'evacuation-coordination',
          states: 4,
          transitions: 5,
        },
      ],
      openApiSpecs: ['at-risk-patients.openapi.json'],
      capabilitiesUsed: ['service.call', 'collection.join', 'collection.filter'],
    },
    null,
    2
  ),

  pages: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-page-otter',
      pages: [
        {
          slug: 'catalog',
          type: 'collection_listing',
          collection: 'products',
          themeApplied: true,
          authRequired: false,
        },
        {
          slug: 'admin',
          type: 'protected',
          collection: null,
          themeApplied: true,
          authRequired: true,
        },
      ],
    },
    null,
    2
  ),

  dashboard: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-dashboard-otter',
      pages: [
        {
          slug: 'dashboard',
          layout: '<grid|table|mixed>',
          collections: ['equipment', 'supplies'],
          mode: '<ISR|Pulse>',
        },
      ],
    },
    null,
    2
  ),

  // type: 'pki' = CAC/DoD certificate auth | 'oidc' = enterprise SSO
  // For dev-only mock auth: use type: 'oidc' with devOnly: true (Zod strips devOnly — it's a convention only)
  auth: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-auth-otter',
      authConfig: {
        type: '<pki|oidc>',
        // OIDC-only fields (omit for pki):
        provider: '<azure_ad|okta|cognito|auth0|authentik|keycloak|custom>',
        discoveryUrl: '<IdP OIDC discovery URL>',
        clientId: '<OIDC client ID>',
        clientSecret: '<OIDC client secret>',
        rbacRoles: ['ADMIN', 'ANALYST'],
        rbacDefaultRole: 'ANALYST',
        protectedRoutes: ['/dashboard/:path*', '/procurement/:path*'],
        auditEnabled: true,
        auditRetentionDays: 90,
      },
      // devScripts is OPTIONAL — only present when devOnly: true was used
      // Polish otter and foreman MUST check devScripts.written before referencing scripts
      devScripts: {
        written: true,
        scripts: { 'dev:admin': 'MOCK_USER=admin next dev' },
      },
    },
    null,
    2
  ),

  polish: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-polish-otter',
      landingPage: { slug: '', rewritten: true },
      navigation: { itemCount: 5, items: ['/dashboard', '/equipment', '/workflows'] },
      gettingStarted: '<rewritten|redirected|not-found>',
      scaffoldCleanup: {
        deleted: ['content/posts/getting-started.yaml'],
        rewritten: ['app/not-found.tsx'],
        skipped: [],
      },
    },
    null,
    2
  ),

  qa: JSON.stringify(
    {
      version: '1.0',
      generatedBy: 'stackwright-pro-qa-otter',
      // skipped: true path — when dev server is unreachable at audit time
      // skipped: false path — full audit completed
      skipped: false,
      skipReason: null,
      wcagLevel: '<AA|AAA>',
      summary: {
        routesAudited: 3,
        serious: 0,
        moderate: 1,
        minor: 0,
      },
      findings: [
        {
          id: 'qa-001',
          route: '/dashboard',
          severity: '<serious|moderate|minor>',
          category: '<visual|a11y|design-contract|runtime>',
          finding: 'Human-readable description of what was observed',
          evidence: {
            screenshot: '.stackwright/qa/screenshots/dashboard-light.png',
            consoleErrors: [],
            axeViolations: [],
          },
          suggested_otters: ['stackwright-pro-theme-otter'],
          suggested_fix: 'Specific, concrete next step the repair otter should take',
        },
      ],
    },
    null,
    2
  ),
};

function _validateArtifactInner(input: ValidateArtifactInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const { phase, responseText, artifact: directArtifact } = input;

  if (!isValidPhase(phase)) {
    return {
      text: JSON.stringify({ error: true, message: `Unknown phase: ${phase}` }),
      isError: true,
    };
  }

  let artifact: Record<string, unknown>;

  if (directArtifact) {
    // Direct artifact path (specialist calling with artifact object):
    // Skip off-script detection and JSON string parsing — already an object.
    artifact = directArtifact;
  } else {
    // Legacy responseText path (Foreman-mediated):
    const text = responseText ?? '';

    // 1. Off-script detection — check the RAW response before trying to parse
    for (const { pattern, label } of OFF_SCRIPT_PATTERNS) {
      if (pattern.test(text)) {
        const result: InvalidArtifactResult = {
          valid: false,
          phase,
          violation: 'off-script',
          retryPrompt: `You returned code output (detected: ${label}). Return ONLY a JSON artifact — no code, no files.`,
        };
        return { text: JSON.stringify(result), isError: false };
      }
    }

    // 2. JSON extraction
    try {
      artifact = extractJsonFromResponse(text) as Record<string, unknown>;
    } catch {
      const result: InvalidArtifactResult = {
        valid: false,
        phase,
        violation: 'invalid-json',
        retryPrompt:
          'Your response did not contain valid JSON. Return a single JSON object with no surrounding text.',
      };
      return { text: JSON.stringify(result), isError: false };
    }
  }

  // 3. Required universal fields
  if (!artifact.version || !artifact.generatedBy) {
    const result: InvalidArtifactResult = {
      valid: false,
      phase,
      violation: 'missing-fields',
      retryPrompt:
        'Your JSON artifact is missing required fields. Every artifact MUST include "version" and "generatedBy" at the top level.',
    };
    return { text: JSON.stringify(result), isError: false };
  }

  // 4. Phase-specific key checks
  const requiredKeys = PHASE_REQUIRED_KEYS[phase];
  const missingKeys = requiredKeys.filter((k) => !(k in artifact));
  if (missingKeys.length > 0) {
    const result: InvalidArtifactResult = {
      valid: false,
      phase,
      violation: 'schema-mismatch',
      retryPrompt: `Your ${phase} artifact is missing required keys: ${missingKeys.join(', ')}. Include them and try again.`,
    };
    return { text: JSON.stringify(result), isError: false };
  }

  // 5. Zod schema validation (where canonical schemas are available)
  //    Provides richer error messages than key-presence checks alone.
  const PHASE_ZOD_VALIDATORS: Partial<
    Record<
      Phase,
      (artifact: Record<string, unknown>) => { success: boolean; error?: { message: string } }
    >
  > = {
    workflow: (artifact) => {
      // The workflow otter produces { version, generatedBy, workflow: { id, route, files, ... } }
      // Primary key: 'workflow'. Defensive: accept 'workflowConfig' as a deprecated alias.
      const workflowData = artifact['workflow'];
      if (!workflowData) {
        if (artifact['workflowConfig']) {
          // swp-k7cl: 'workflowConfig' is the old key name; warn and accept so the run
          // still succeeds while the otter prompt change propagates.
          console.warn(
            '[pipeline] DEPRECATED: workflow artifact uses "workflowConfig" key; rename to "workflow"'
          );
          return { success: true };
        }
        // No workflow/workflowConfig key — fine, PHASE_REQUIRED_KEYS only checks version/generatedBy
        return { success: true };
      }
      // Optionally deep-validate if the artifact contains the full workflow file content
      // (i.e., the otter embedded { workflow: { id, label, steps } } in the workflow key).
      // Skip if the value is plain metadata (no nested 'workflow' key).
      if (typeof workflowData === 'object' && workflowData !== null && 'workflow' in workflowData) {
        const result = WorkflowFileSchema.safeParse(workflowData);
        if (!result.success) {
          const issues = result.error.issues
            .slice(0, 3)
            .map((i) => `${i.path.join('.')}: ${i.message}`)
            .join('; ');
          return { success: false, error: { message: issues } };
        }
      }
      return { success: true };
    },
    auth: (artifact) => {
      // The auth otter produces { version, generatedBy, authConfig: { type: 'oidc'|'pki', ... } }
      const authConfig = artifact['authConfig'];
      if (!authConfig) return { success: true }; // skip if not present
      const result = authConfigSchema.safeParse(authConfig);
      if (!result.success) {
        const issues = result.error.issues
          .slice(0, 3)
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; ');
        return { success: false, error: { message: issues } };
      }

      // swp-owu0 — defense-in-depth: reject RBAC theatre.
      // Fires when: >2 roles defined AND all protected routes share a single
      // requiredRole AND that role is the defaultRole. A 2-role setup
      // (e.g., ADMIN + USER, all routes require USER) is a legitimate flat
      // design and is intentionally exempted from this check.
      const rbacRoles = (authConfig as Record<string, unknown>)['rbacRoles'];
      const rbacDefaultRole = (authConfig as Record<string, unknown>)['rbacDefaultRole'];
      const protectedRoutes = (authConfig as Record<string, unknown>)['protectedRoutes'];
      if (
        Array.isArray(rbacRoles) &&
        rbacRoles.length > 2 &&
        Array.isArray(protectedRoutes) &&
        protectedRoutes.length > 0 &&
        typeof rbacDefaultRole === 'string'
      ) {
        type RouteEntry = string | { pattern: string; requiredRole?: string };
        const uniqueRoles = new Set(
          (protectedRoutes as RouteEntry[]).map((r) =>
            typeof r === 'string' ? rbacDefaultRole : (r.requiredRole ?? rbacDefaultRole)
          )
        );
        if (uniqueRoles.size === 1 && uniqueRoles.has(rbacDefaultRole)) {
          return {
            success: false,
            error: {
              message:
                `RBAC theatre detected: all ${protectedRoutes.length} protectedRoutes require ` +
                `'${rbacDefaultRole}' (the defaultRole), but ${rbacRoles.length} roles are defined. ` +
                `This defeats the RBAC hierarchy. Each route MUST have the minimum role required ` +
                `for its function. See auth-otter PER-ROUTE RBAC GRANULARITY section.`,
            },
          };
        }
      }

      return { success: true };
    },

    // swp-4071 — opt-in cross-reference validator for the services phase.
    // If workflow-config exists and declares serviceHooks, every hook ref must resolve
    // to a flow or state-machine in this services-config artifact.
    // Skips gracefully when wizard-otter didn't run (Pro-only install) or declared no hooks.
    services: (artifact) => {
      const workflowArtifactPath = join(cwd, '.stackwright', 'artifacts', 'workflow-config.json');
      if (!existsSync(workflowArtifactPath)) {
        // No workflow artifact — wizard-otter didn't run for this project. Skip.
        return { success: true };
      }

      let workflowArtifact: {
        serviceHooks?: Array<{ ref: string; kind?: string; purpose?: string }>;
      };
      try {
        workflowArtifact = JSON.parse(readFileSync(workflowArtifactPath, 'utf-8')) as {
          serviceHooks?: Array<{ ref: string; kind?: string; purpose?: string }>;
        };
      } catch {
        // Workflow artifact corrupt — that's the workflow phase's problem, not ours. Skip.
        return { success: true };
      }

      const declaredHooks = workflowArtifact.serviceHooks ?? [];
      if (declaredHooks.length === 0) {
        return { success: true }; // wizard declared no hooks
      }

      // Extract flow + state-machine names from this services-config artifact.
      // The services artifact has `flows` and `workflows` (state-machines) at the
      // top level — NOT nested under a 'services' key.
      const flows = (artifact['flows'] as Array<{ name: string }> | undefined) ?? [];
      const workflows = (artifact['workflows'] as Array<{ name: string }> | undefined) ?? [];
      const flowNames = new Set([...flows.map((f) => f.name), ...workflows.map((w) => w.name)]);

      const unresolved = declaredHooks.filter((h) => !flowNames.has(h.ref));
      if (unresolved.length > 0) {
        return {
          success: false,
          error: {
            message:
              `Workflow declared ${declaredHooks.length} service hook(s) but ` +
              `${unresolved.length} unresolved by services-config: ` +
              unresolved
                .map((h) => `${h.ref} (${h.kind ?? 'unknown'}: ${h.purpose ?? 'no purpose'})`)
                .join('; ') +
              '. Either add matching flows to services-config OR remove the hooks from the workflow YAML.',
          },
        };
      }

      return { success: true };
    },

    // qa artifact — permissive validator: requires version + generatedBy + skipped.
    // When skipped=true: skipReason must be a string.
    // When skipped=false: findings must be an array and summary must be an object.
    qa: (artifact) => {
      const skipped = artifact['skipped'];
      if (typeof skipped !== 'boolean') {
        return {
          success: false,
          error: { message: '"skipped" must be a boolean (true or false).' },
        };
      }

      if (skipped === true) {
        if (typeof artifact['skipReason'] !== 'string') {
          return {
            success: false,
            error: { message: 'When skipped=true, "skipReason" must be a string.' },
          };
        }
        return { success: true };
      }

      // skipped=false: require findings array and summary object
      if (!Array.isArray(artifact['findings'])) {
        return {
          success: false,
          error: { message: 'When skipped=false, "findings" must be an array.' },
        };
      }
      if (typeof artifact['summary'] !== 'object' || artifact['summary'] === null) {
        return {
          success: false,
          error: { message: 'When skipped=false, "summary" must be an object.' },
        };
      }
      return { success: true };
    },
  };

  const zodValidator = PHASE_ZOD_VALIDATORS[phase];
  if (zodValidator) {
    const zodResult = zodValidator(artifact);
    if (!zodResult.success) {
      const result: InvalidArtifactResult = {
        valid: false,
        phase,
        violation: 'schema-mismatch',
        retryPrompt: `Your artifact failed schema validation: ${zodResult.error?.message}. Fix these fields and return the corrected JSON artifact.`,
      };
      return { text: JSON.stringify(result), isError: false };
    }
  }

  // 5.5 Pulse content.yml validation (swp-l6yu)
  //
  // For phases that produce pages/**\/content.yml, validate every Pulse
  // content_item against its real Zod schema from @stackwright-pro/pulse.
  // Catches adjacent-wrong-prop-name bugs (valueField vs field, label vs header)
  // before the artifact is written — forcing a retry instead of a silent ship.
  //
  // Runs AFTER JSON/schema checks so the otter gets the simpler error first.
  if (PULSE_CONTENT_PHASES.has(phase)) {
    const pulseResult = validatePulseContent(cwd);
    if (!pulseResult.valid) {
      const result: InvalidArtifactResult = {
        valid: false,
        phase,
        violation: 'schema-mismatch',
        retryPrompt: formatPulseRetryPrompt(pulseResult),
      };
      return { text: JSON.stringify(result), isError: false };
    }
  }

  // 6. All good — write it and sign it
  try {
    const artifactsDir = join(cwd, '.stackwright', 'artifacts');
    mkdirSync(artifactsDir, { recursive: true });
    // Per-integration fan-out: when phase='api' + integrationName supplied, write
    // to api-config-<integrationName>.json so concurrent invocations don't collide.
    const artifactFile =
      phase === 'api' && input.integrationName
        ? `api-config-${input.integrationName}.json`
        : PHASE_ARTIFACT[phase];
    const artifactPath = join(artifactsDir, artifactFile);

    // Serialize once — sign and write the same bytes (zero TOCTOU window)
    const serialized = JSON.stringify(artifact, null, 2) + '\n';
    const artifactBytes = Buffer.from(serialized, 'utf-8');
    safeWriteSync(artifactPath, serialized);

    // Sign the artifact (non-blocking — signing failure doesn't prevent write)
    let signed = false;
    try {
      const { privateKey } = loadPipelineKeys(cwd);
      const sig = signArtifact(artifactBytes, privateKey);
      const signerOtter = PHASE_TO_OTTER[phase];
      saveArtifactSignature(cwd, artifactFile, sig, signerOtter);
      signed = true;
    } catch {
      // Signing is defense-in-depth — pipeline continues without signatures
      // if keys haven't been initialized (e.g., backwards-compat with old pipelines)
    }

    // Update pipeline state
    updateStateAtomic(cwd, (state) => {
      if (!state.phases[phase]) state.phases[phase] = defaultPhaseStatus();
      const ps = state.phases[phase]!; // guaranteed by line above
      ps.artifactWritten = true;
    });

    const topKeys = Object.keys(artifact).slice(0, 5).join(', ');
    const result: ValidArtifactResult = {
      valid: true,
      phase,
      artifactPath,
      summary: `Wrote ${artifactFile} (keys: ${topKeys}${Object.keys(artifact).length > 5 ? ', ...' : ''})${signed ? ' [signed]' : ''}`,
    };
    return { text: JSON.stringify(result), isError: false };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { text: JSON.stringify({ error: true, phase, message }), isError: true };
  }
}

/**
 * Public API — wraps _validateArtifactInner with best-effort telemetry (swp-im3c).
 * All emit() calls are fire-and-forget; failures never surface to callers.
 */
export function handleValidateArtifact(input: ValidateArtifactInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const { phase } = input;
  // Telemetry: best-effort, never blocks (swp-im3c)
  emit(
    { type: 'tool_call', toolName: 'stackwright_pro_validate_artifact', args: { phase }, phase },
    { cwd }
  );
  const result = _validateArtifactInner(input);
  try {
    const parsed = JSON.parse(result.text) as { valid?: boolean; artifactPath?: string };
    if (parsed.valid === true && parsed.artifactPath) {
      // Telemetry: best-effort, never blocks (swp-im3c)
      emit({ type: 'file_write', path: parsed.artifactPath, phase }, { cwd });
    }
    // Telemetry: best-effort, never blocks (swp-im3c)
    emit(
      {
        type: 'tool_complete',
        toolName: 'stackwright_pro_validate_artifact',
        success: parsed.valid === true,
        phase,
      },
      { cwd }
    );
  } catch {
    // Telemetry parse/emit errors never surface to callers
  }
  return result;
}

// ---------------------------------------------------------------------------
// handleEmitEvent — authorization-aware foreman-only lifecycle event handler (swp-4dbg)
// ---------------------------------------------------------------------------

/**
 * Input type for handleEmitEvent — mirrors the server.tool() schema after Zod parsing.
 * `success` arrives as boolean (string coercion handled at the Zod layer via boolCoerce).
 */
export interface EmitEventInput {
  type:
    | 'phase_start'
    | 'phase_complete'
    | 'phase_ready'
    | 'agent_invoke_start'
    | 'agent_invoke_complete';
  // Optional fields typed as `T | undefined` (not just `T`) so Zod's .optional()
  // destructuring (which produces string | undefined) satisfies exactOptionalPropertyTypes.
  phase?: string | undefined;
  targetOtter?: string | undefined;
  success?: boolean | undefined;
  otter?: string | undefined;
  durationSec?: number | undefined;
  /** @internal — test injection hook. Omit to use the real telemetry emit(). */
  _emit?: typeof emit;
}

/**
 * Authorization-aware handler for stackwright_pro_emit_event (swp-4dbg).
 *
 * Security contract:
 * - Only 'foreman' may emit lifecycle events.
 * - Non-foreman callers receive { emitted: false, reason: "AUTHORIZATION: ..." }
 *   with isError: false — an informational rejection, not a tool-call failure.
 *   This prevents LLM re-invocation cascades that would arise from a failed tool call.
 * - `success` arg: accept boolean OR string "true"/"false" (coerced at Zod layer).
 * - Defense in depth: any unexpected throw returns { emitted: false, reason: ... }.
 *   This handler NEVER throws.
 *
 * Exported for unit testing. The MCP server.tool() registration delegates here.
 */
export function handleEmitEvent(input: EmitEventInput): { text: string; isError: boolean } {
  const emitFn = input._emit ?? emit;
  try {
    const caller = input.otter ?? 'foreman';
    if (caller !== 'foreman') {
      return {
        text: JSON.stringify({
          emitted: false,
          reason:
            'AUTHORIZATION: only the foreman otter may emit lifecycle events. ' +
            'Specialist otters should signal completion via their response text ' +
            'and the artifact write — not by emitting phase events. ' +
            `Caller was "${caller}". This is informational, not a failure.`,
        }),
        isError: false,
      };
    }

    let emitted = false;
    if (input.type === 'phase_start') {
      if (!input.phase) {
        return {
          text: JSON.stringify({ emitted: false, reason: 'phase required for phase_start' }),
          isError: false,
        };
      }
      emitted = emitFn({ type: 'phase_start', phase: input.phase, otter: caller });
    } else if (input.type === 'phase_ready') {
      if (!input.phase) {
        return {
          text: JSON.stringify({ emitted: false, reason: 'phase required for phase_ready' }),
          isError: false,
        };
      }
      emitted = emitFn({ type: 'phase_ready', phase: input.phase, otter: caller });
    } else if (input.type === 'phase_complete') {
      if (!input.phase) {
        return {
          text: JSON.stringify({ emitted: false, reason: 'phase required for phase_complete' }),
          isError: false,
        };
      }
      emitted = emitFn({
        type: 'phase_complete',
        phase: input.phase,
        otter: caller,
        ...(input.durationSec != null ? { durationSec: input.durationSec } : {}),
      });
    } else if (input.type === 'agent_invoke_start') {
      emitted = emitFn({
        type: 'agent_invoke_start',
        targetOtter: input.targetOtter ?? '',
        phase: input.phase,
        otter: caller,
      });
    } else if (input.type === 'agent_invoke_complete') {
      emitted = emitFn({
        type: 'agent_invoke_complete',
        targetOtter: input.targetOtter ?? '',
        success: input.success ?? true,
        phase: input.phase,
        otter: caller,
        ...(input.durationSec != null ? { durationSec: input.durationSec } : {}),
      });
    }

    return { text: JSON.stringify({ emitted }), isError: false };
  } catch (err) {
    // Defense-in-depth: any unexpected error returns a clean response. Never throws.
    const reason = err instanceof Error ? err.message : String(err);
    return {
      text: JSON.stringify({ emitted: false, reason: `telemetry error (non-fatal): ${reason}` }),
      isError: false,
    };
  }
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerPipelineTools(server: McpServer): void {
  const DESC = 'Writes state to .stackwright/ — the filesystem is the state machine.';
  const res = (r: { text: string; isError: boolean }) => ({
    content: [{ type: 'text' as const, text: r.text }],
    isError: r.isError,
  });

  server.tool(
    'stackwright_pro_get_pipeline_state',
    `Read pipeline state from .stackwright/pipeline-state.json. ${DESC}`,
    {},
    async () => res(handleGetPipelineState())
  );

  server.tool(
    'stackwright_pro_set_pipeline_state',
    `Atomic read→modify→write pipeline state. ${DESC}`,
    {
      phase: z.string().optional().describe('Phase to update, e.g. "designer"'),
      field: z
        .enum(['questionsCollected', 'answered', 'executed', 'artifactWritten', 'inFlight'])
        .optional()
        .describe('Boolean field to set'),
      // inFlight: set true BEFORE specialist invoke (dataflow mode, swp-ioc7); false after.
      value: boolCoerce(z.boolean().optional()).describe(
        'Value for the field — must be a JSON boolean (true/false), NOT the string "true"/"false"'
      ),
      status: z
        .enum(['setup', 'questions', 'execution', 'done'])
        .optional()
        .describe('Top-level status override'),
      incrementRetry: boolCoerce(z.boolean().optional()).describe(
        'Bump retryCount by 1 — must be a JSON boolean'
      ),
      updates: jsonCoerce(
        z
          .array(
            z.object({
              phase: z.string(),
              field: z.enum([
                'questionsCollected',
                'answered',
                'executed',
                'artifactWritten',
                'inFlight',
              ]),
              value: z.boolean(),
            })
          )
          .optional()
      ).describe('Batch updates — apply multiple field changes in one atomic read→modify→write'),
    },
    async (args) =>
      res(
        handleSetPipelineState({
          ...(args.phase != null ? { phase: args.phase } : {}),
          ...(args.field != null ? { field: args.field } : {}),
          ...(args.value != null ? { value: args.value } : {}),
          ...(args.status != null ? { status: args.status } : {}),
          ...(args.incrementRetry != null ? { incrementRetry: args.incrementRetry } : {}),
          ...(args.updates != null ? { updates: args.updates as BatchStateUpdate[] } : {}),
        })
      )
  );

  server.tool(
    'stackwright_pro_check_execution_ready',
    `Check all phases have answer files in .stackwright/answers/. If phase is provided, check only that phase. ${DESC}`,
    {
      phase: z
        .string()
        .optional()
        .describe("If provided, check only this phase's readiness. Omit to check all phases."),
    },
    async ({ phase }) => res(handleCheckExecutionReady(undefined, phase))
  );

  server.tool(
    'stackwright_pro_get_ready_phases',
    `Return phases whose dependencies are all satisfied (executed=true). Use to determine which phases can run next — enables wave-based execution. ${DESC}`,
    {},
    async () => res(handleGetReadyPhases())
  );

  server.tool(
    'stackwright_pro_list_artifacts',
    `List phase artifacts in .stackwright/artifacts/ with completedCount/totalCount. ${DESC}`,
    {},
    async () => res(handleListArtifacts())
  );

  server.tool(
    'stackwright_pro_write_phase_questions',
    `Parse otter question-collection response → .stackwright/questions/{phase}.json. Specialists may also call this directly with a parsed questions array. ${DESC}`,
    {
      phase: z
        .string()
        .optional()
        .describe('Phase name, e.g. "designer" (required for direct write)'),
      responseText: z
        .string()
        .optional()
        .describe('Raw LLM response from QUESTION_COLLECTION_MODE'),
      questions: jsonCoerce(z.array(z.any()).optional()).describe(
        'Questions array for direct specialist write'
      ),
    },
    async ({ phase, responseText, questions }) => {
      // Direct write path: specialist calls with parsed array (no responseText parsing)
      if (phase && questions && Array.isArray(questions)) {
        if (!SAFE_PHASE.test(phase)) {
          return {
            content: [
              { type: 'text' as const, text: JSON.stringify({ error: `Invalid phase name` }) },
            ],
            isError: true,
          };
        }
        const questionsDir = join(process.cwd(), '.stackwright', 'questions');
        mkdirSync(questionsDir, { recursive: true });
        const outPath = join(questionsDir, `${phase}.json`);
        writeFileSync(
          outPath,
          JSON.stringify({ phase, questions, writtenAt: new Date().toISOString() }, null, 2)
        );
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({
                written: true,
                phase,
                count: (questions as unknown[]).length,
              }),
            },
          ],
        };
      }
      // Fallback: parse from raw LLM responseText
      return res(
        handleWritePhaseQuestions({ phase: phase ?? '', responseText: responseText ?? '' })
      );
    }
  );

  server.tool(
    'stackwright_pro_build_specialist_prompt',
    `Assemble execution prompt from answers + upstream artifacts. Foreman passes verbatim. ${DESC}`,
    { phase: z.string().describe('Phase to build prompt for, e.g. "pages"') },
    async ({ phase }) => res(handleBuildSpecialistPrompt({ phase }))
  );

  server.tool(
    'stackwright_pro_validate_artifact',
    `Validate and write artifact to .stackwright/artifacts/. Returns retryPrompt on failure. ${DESC}`,
    {
      phase: z.string().describe('Phase that produced this artifact, e.g. "designer"'),
      responseText: z
        .string()
        .optional()
        .describe('Raw response text from the specialist otter (Foreman-mediated path, legacy)'),
      artifact: jsonCoerce(z.record(z.string(), z.unknown()).optional()).describe(
        'Artifact object to validate and write directly (specialist direct path — skips off-script detection and JSON parsing)'
      ),
      integrationName: z
        .string()
        .optional()
        .describe(
          'When phase="api": write to api-config-<integrationName>.json instead of api-config.json. Enables concurrent per-spec fan-out without collision (swp-530y).'
        ),
    },
    async ({ phase, responseText, artifact, integrationName }) => {
      if (artifact) {
        return res(
          handleValidateArtifact({
            phase,
            artifact: artifact as Record<string, unknown>,
            ...(integrationName ? { integrationName } : {}),
          })
        );
      }
      return res(
        handleValidateArtifact({
          phase,
          responseText: responseText ?? '',
          ...(integrationName ? { integrationName } : {}),
        })
      );
    }
  );

  // ---------------------------------------------------------------------------
  // Foreman lifecycle emission tool (swp-im3c, authz hardened swp-4dbg)
  // FOREMAN ONLY — specialist otters receive a clean rejection (no cascade).
  // ---------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_emit_event',
    `FOREMAN ONLY — specialist otters that call this will receive an authorization rejection (no-op, not an error). ` +
      `Emit a telemetry event to .stackwright/pipeline-events.ndjson. ` +
      `Foreman calls this at phase boundaries and agent-invoke boundaries. ` +
      `Best-effort: failures are silent and never block. ${DESC}`,
    {
      type: z
        .enum([
          'phase_start',
          'phase_complete',
          'phase_ready',
          'agent_invoke_start',
          'agent_invoke_complete',
        ])
        .describe('Event type to emit'),
      // phase_ready: emitted in dataflow mode (swp-ioc7) when a phase enters the ready set
      // (deps satisfied, not inFlight, not yet complete) — BEFORE invoking the specialist.
      phase: z.string().optional().describe('Current phase name'),
      targetOtter: z
        .string()
        .optional()
        .describe('For agent_invoke_* events: which otter is being invoked'),
      success: boolCoerce(z.boolean().optional()).describe(
        'For agent_invoke_complete: did the invocation succeed? (boolean; string "true"/"false" also accepted for caller tolerance)'
      ),
      otter: z.string().optional().describe('Which otter is emitting (defaults to "foreman")'),
      durationSec: z.number().optional().describe('Duration in seconds (for *_complete events)'),
    },
    async ({ type, phase, targetOtter, success, otter, durationSec }) =>
      res(handleEmitEvent({ type, phase, targetOtter, success, otter, durationSec }))
  );
}
